"use client";

import Image from "next/image";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import "swiper/css";

import { Link } from "@/navigation";

interface ResourceCard {
	keyPrefix: string;
	image: string;
	href: string;
}

interface IndustryResourcesProps {
	namespace: string;
	resources: readonly ResourceCard[];
	brandColor?: string;
}

export default function IndustryResources({
	namespace,
	resources,
	brandColor = "#64a525",
}: IndustryResourcesProps) {
	const t = useTranslations(namespace);
	const imageSizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-6 text-center text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
					{t("resourcesTitle")}
				</h2>

				<Swiper
					spaceBetween={24}
					slidesPerView={1}
					loop={resources.length > 3}
					breakpoints={{
						640: { slidesPerView: 2 },
						1024: { slidesPerView: 3 },
					}}
					className="!pb-20"
				>
					{resources.map(({ keyPrefix, image, href }) => (
						<SwiperSlide key={keyPrefix} className="!h-auto">
							<article className="flex h-full flex-col overflow-hidden bg-[#fcfff4] shadow-card transition-shadow duration-300 hover:shadow-md">
								<div className="relative aspect-[4/2.5] w-full overflow-hidden">
									<Image
										src={image}
										alt={t(`${keyPrefix}ImageAlt`)}
										fill
										sizes={imageSizes}
										className="object-cover"
									/>
								</div>

								<div className="flex flex-1 flex-col gap-3 p-5 sm:p-6">
									<h3 className="text-lg font-bold leading-snug text-black sm:text-xl">
										{t(`${keyPrefix}Title`)}
									</h3>
									<p className="flex-1 text-sm leading-relaxed text-black md:text-base">
										{t(`${keyPrefix}Desc`)}
									</p>
									<Link
										href={href}
										className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200 hover:opacity-75"
										style={{ color: brandColor }}
									>
										{t(`${keyPrefix}Link`)}
										<ArrowRight size={16} weight="bold" />
									</Link>
								</div>
							</article>
						</SwiperSlide>
					))}
					<ResourceCarouselControls />
				</Swiper>
			</div>
		</section>
	);
}

function ResourceCarouselControls() {
	const swiper = useSwiper();

	return (
		<div className="absolute bottom-0 left-1/2 z-10 flex -translate-x-1/2 items-center gap-5">
			<button
				type="button"
				aria-label="Previous resource"
				className="flex size-11 items-center justify-center rounded-full border border-gray-400 text-black transition-colors duration-200 hover:border-[#64a525] hover:text-[#64a525]"
				onClick={() => swiper.slidePrev()}
			>
				<ArrowLeft size={20} />
			</button>
			<button
				type="button"
				aria-label="Next resource"
				className="flex size-11 items-center justify-center rounded-full border border-gray-400 text-black transition-colors duration-200 hover:border-[#64a525] hover:text-[#64a525]"
				onClick={() => swiper.slideNext()}
			>
				<ArrowRight size={20} />
			</button>
		</div>
	);
}
